import { ApiError } from "../lib/api";

/** Restore only an existing session; never create a session or replay a command. */
export function restoreActiveSession<T>(callbacks: {
  load(signal: AbortSignal): Promise<{ session: T | null }>;
  restore(session: T): void;
  notice(message: string): void;
}) {
  let stopped = false;
  let inFlight = false;
  let attempts = 0;
  let retryNotBefore = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let activeRequest: AbortController | null = null;

  function clearTimer() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  async function load() {
    if (stopped || inFlight || typeof navigator !== "undefined" && navigator.onLine === false) return;
    clearTimer();
    if (Date.now() < retryNotBefore) {
      timer = setTimeout(() => { void load(); }, retryNotBefore - Date.now());
      return;
    }
    inFlight = true;
    const request = new AbortController();
    activeRequest = request;
    const timeout = setTimeout(() => request.abort(), 12_000);
    try {
      const result = await callbacks.load(request.signal);
      if (stopped) return;
      callbacks.notice("");
      dispose();
      if (result.session) callbacks.restore(result.session);
    } catch (error) {
      if (stopped) return;
      if (error instanceof ApiError && error.status < 500 && error.status !== 429) {
        callbacks.notice(error.message);
        dispose();
        return;
      }
      callbacks.notice("Die laufende Sitzung wird gesucht. Die Verbindung wird automatisch erneut geprüft.");
      const delay = error instanceof ApiError && error.status === 429 ? 12_000 : Math.min(30_000, 1_000 * 2 ** Math.min(attempts++, 5));
      if (error instanceof ApiError && error.status === 429) retryNotBefore = Date.now() + delay;
      timer = setTimeout(() => { void load(); }, delay);
    } finally {
      clearTimeout(timeout);
      activeRequest = null;
      inFlight = false;
    }
  }

  function wake() {
    if (document.visibilityState !== "hidden") void load();
  }

  function dispose() {
    stopped = true;
    clearTimer();
    activeRequest?.abort();
    if (typeof window !== "undefined") {
      window.removeEventListener("online", wake);
      window.removeEventListener("pageshow", wake);
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
    }
  }

  if (typeof window !== "undefined") {
    window.addEventListener("online", wake);
    window.addEventListener("pageshow", wake);
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);
  }
  void load();
  return dispose;
}
